import React, { useState, cloneElement } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FormattedMessage } from '../../util/reactIntl';

/**
 * ConfirmDialog Component - Wraps children with confirmation dialog
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The clickable trigger element
 * @param {Function} props.onConfirm - Function to call when user confirms
 * @param {string|React.ReactNode} props.title - Dialog title
 * @param {string|React.ReactNode} props.message - Dialog message
 * @param {string} props.confirmText - Text for confirm button (default: "Delete")
 * @param {string} props.cancelText - Text for cancel button (default: "Cancel")
 * @param {string} props.confirmColor - Color for confirm button (default: "error")
 * @param {boolean} props.loading - Whether the confirm action is loading
 * @param {boolean} props.disabled - Whether the trigger should be disabled
 */
const ConfirmDialog = ({
  children,
  onConfirm,
  title,
  message,
  confirmText = 'Delete',
  cancelText = 'Cancel',
  confirmColor = 'error',
  loading = false,
  disabled = false,
  onClose = () => {},
}) => {
  const [open, setOpen] = useState(false);

  const handleOpen = (event) => {
    if (disabled) return;
    event.preventDefault();
    event.stopPropagation();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const handleConfirm = async () => {
    try {
      await onConfirm();
      setOpen(false);
    } catch (error) {
      // Keep dialog open if there's an error
      console.error('Confirm action failed:', error);
    }
  };

  // Clone the child element and add our click handler
  const trigger = cloneElement(children, {
    onClick: handleOpen,
    disabled: disabled || loading,
  });

  return (
    <>
      {trigger}
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="confirm-dialog-title">
          {title || <FormattedMessage id="ConfirmDialog.defaultTitle" defaultMessage="Confirm Action" />}
          <IconButton
            aria-label="close"
            onClick={() => handleClose()}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            {message || <FormattedMessage id="ConfirmDialog.defaultMessage" defaultMessage="Are you sure you want to proceed?" />}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleClose} 
            color="primary"
            disabled={loading}
          >
            {cancelText}
          </Button>
          <Button 
            onClick={handleConfirm} 
            color={confirmColor}
            variant="contained"
            disabled={loading}
            autoFocus
          >
            {loading ? 'Processing...' : confirmText}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConfirmDialog;